export function parseFit(value) {
  const match = String(value).match(/^\s*(\d+(?:\.\d+)?)\s*W\s*[x×]\s*(\d+(?:\.\d+)?)\s*L\s*$/i);
  return match ? { waist: match[1], inseam: match[2], value } : null;
}

export function fitState(variants, index, selections, values) {
  const pairs = values.map(parseFit);
  if (!pairs.length || pairs.some(pair => !pair) || !variants.length) return null;
  const selected = parseFit(selections[index]);
  if (!selected) return null;
  const available = variants.filter(variant => variant.available && selections.every((value, i) => i === index || variant.options[i] === value));
  const unique = key => [...new Set(pairs.map(pair => pair[key]))].sort((a, b) => Number(a) - Number(b));
  const actualWaists = new Set(variants.map(variant => parseFit(variant.options[index])).filter(Boolean).map(pair => Number(pair.waist)));
  const waistGroupsEnabled = [42, 44, 46].every(waist => actualWaists.has(waist));
  return { pairs, selected, available, index, waists: unique('waist'), inseams: unique('inseam'), waistGroupsEnabled };
}

export function chooseFit(state, axis, value) {
  const matches = state.pairs.filter(pair => pair[axis] === value && (axis === 'waist' || pair.waist === state.selected.waist) && state.available.some(variant => variant.options[state.index] === pair.value));
  return matches.find(pair => pair.inseam === state.selected.inseam) || matches.sort((a, b) => Number(a.inseam) - Number(b.inseam))[0] || null;
}

export function waistsForGroup(waists, group) {
  return waists.filter(value => group === 'big' ? Number(value) >= 42 : Number(value) <= 42);
}

export function selectedWaistGroup(state, preferred) {
  if (!state.waistGroupsEnabled) return null;
  if (preferred && waistsForGroup([state.selected.waist], preferred).length) return preferred;
  return Number(state.selected.waist) > 42 ? 'big' : 'standard';
}

export function chooseWaistGroup(state, group) {
  if (!state.waistGroupsEnabled) return null;
  const waists = waistsForGroup(state.waists, group);
  if (waists.includes(state.selected.waist)) return state.selected;
  const choices = waists.map(waist => chooseFit(state, 'waist', waist)).filter(Boolean);
  return choices.find(pair => pair.inseam === state.selected.inseam) || choices[0] || null;
}

if (typeof window !== 'undefined' && !customElements.get('loogu-product-options')) {
  class LooguProductOptions extends HTMLElement {
    connectedCallback() {
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this, { childList: true });
      this.refresh();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      clearTimeout(this.timeout);
    }

    refresh() {
      const picker = [...this.querySelectorAll(':scope > variant-picker')].find(node => node.style.display !== 'none');
      if (!picker || picker === this.picker) return;
      clearTimeout(this.timeout);
      this.picker = picker;
      this.root?.remove();
      this.root = null;
      this.busy = false;
      const fields = [...picker.querySelectorAll(':scope > fieldset')];
      fields.forEach(field => field.classList.toggle('loogu-color-option', !!field.querySelector('.color-swatch')));
      const field = fields.find(node => {
        const inputs = [...node.querySelectorAll('input[data-option-value-id]')];
        return inputs.length && inputs.every(input => parseFit(input.value));
      });
      if (!field) return;
      let variants;
      try { variants = JSON.parse(picker.querySelector('[data-variants]')?.textContent || '[]'); } catch { return; }
      const inputs = [...field.querySelectorAll('input[data-option-value-id]')];
      const index = Number(inputs[0].dataset.optionIndex) - 1;
      const selections = fields.map(node => node.querySelector('input:checked')?.value);
      const state = fitState(variants, index, selections, inputs.map(input => input.value));
      if (!state) return;
      this.state = state;
      this.inputs = inputs;
      this.waistGroup = selectedWaistGroup(state, this.waistGroup);
      const root = document.createElement('div');
      root.className = 'loogu-fit-picker';
      this.root = root;
      const makeGroup = (axis, title, values) => {
        const section = document.createElement('div');
        section.className = 'loogu-fit-group';
        const heading = document.createElement('div');
        heading.className = 'loogu-fit-heading';
        const label = document.createElement('span');
        label.id = picker.id + '-' + axis + '-label';
        label.textContent = title;
        heading.append(label);
        if (axis === 'waist') {
          const chart = field.querySelector('.form__label button[aria-controls]');
          if (chart) {
            const trigger = chart.cloneNode(true);
            trigger.removeAttribute('id');
            heading.append(trigger);
          }
        }
        const group = document.createElement('div');
        group.className = 'loogu-fit-grid';
        group.dataset.axis = axis;
        group.setAttribute('role', 'radiogroup');
        group.setAttribute('aria-labelledby', label.id);
        values.forEach(value => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'loogu-fit-button';
          button.dataset.axis = axis;
          button.dataset.value = value;
          button.textContent = axis === 'inseam' ? value + '\u2033' : value;
          button.setAttribute('role', 'radio');
          button.setAttribute('aria-checked', String(value === state.selected[axis]));
          button.disabled = !chooseFit(state, axis, value);
          button.tabIndex = value === state.selected[axis] && !button.disabled ? 0 : -1;
          button.addEventListener('click', () => this.select(axis, value));
          group.append(button);
        });
        if (!group.querySelector('[tabindex="0"]')) {
          const first = group.querySelector('button:not(:disabled)');
          if (first) first.tabIndex = 0;
        }
        group.addEventListener('keydown', event => {
          const buttons = [...group.querySelectorAll('button:not(:disabled):not([hidden])')];
          const position = buttons.indexOf(event.target);
          if (position < 0 || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const backwards = ['ArrowLeft', 'ArrowUp'].includes(event.key);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (position + (backwards ? -1 : 1) + buttons.length) % buttons.length;
          buttons[next].focus();
          buttons[next].click();
        });
        section.append(heading);
        if (axis === 'waist' && state.waistGroupsEnabled) {
          const tabs = document.createElement('div');
          tabs.className = 'loogu-waist-groups';
          tabs.setAttribute('role', 'group');
          tabs.setAttribute('aria-labelledby', label.id);
          ['standard', 'big'].forEach(name => {
            const tab = document.createElement('button');
            tab.type = 'button';
            tab.className = 'loogu-waist-group';
            tab.dataset.waistGroup = name;
            tab.textContent = this.dataset[name + 'Label'];
            tab.addEventListener('click', () => this.selectWaistGroup(name));
            tabs.append(tab);
          });
          tabs.addEventListener('keydown', event => {
            const buttons = [...tabs.querySelectorAll('button:not(:disabled)')];
            const position = buttons.indexOf(event.target);
            if (position < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (position + (event.key === 'ArrowLeft' ? -1 : 1) + buttons.length) % buttons.length;
            buttons[next].focus();
            buttons[next].click();
          });
          section.append(tabs);
        }
        section.append(group);
        root.append(section);
      };
      makeGroup('waist', this.dataset.waistLabel, state.waists);
      makeGroup('inseam', this.dataset.inseamLabel, state.inseams);
      const status = document.createElement('p');
      status.className = 'loogu-fit-status';
      status.setAttribute('role', 'status');
      root.append(status);
      field.querySelectorAll('x-modal').forEach(modal => root.append(modal));
      this.append(root);
      this.updateWaistGroups();
      field.hidden = true;
      field.classList.add('loogu-combined-option');
      if (this.pendingFocus) {
        const target = this.pendingFocus.group
          ? root.querySelector('[data-waist-group="' + this.waistGroup + '"]')
          : [...root.querySelectorAll('button[data-axis]')].find(button => !button.hidden && button.dataset.axis === this.pendingFocus.axis && button.dataset.value === state.selected[this.pendingFocus.axis]);
        target?.focus({ preventScroll: true });
        this.pendingFocus = null;
      }
    }

    updateWaistGroups() {
      const visible = this.state.waistGroupsEnabled ? waistsForGroup(this.state.waists, this.waistGroup) : this.state.waists;
      this.root.querySelectorAll('[data-waist-group]').forEach(tab => {
        tab.setAttribute('aria-pressed', String(tab.dataset.waistGroup === this.waistGroup));
        tab.disabled = this.busy || !chooseWaistGroup(this.state, tab.dataset.waistGroup);
      });
      const buttons = [...this.root.querySelectorAll('button[data-axis="waist"]')];
      buttons.forEach(button => {
        button.hidden = !visible.includes(button.dataset.value);
        button.tabIndex = !button.hidden && !button.disabled && button.dataset.value === this.state.selected.waist ? 0 : -1;
      });
      if (!buttons.some(button => button.tabIndex === 0)) {
        const first = buttons.find(button => !button.hidden && !button.disabled);
        if (first) first.tabIndex = 0;
      }
    }

    selectWaistGroup(group) {
      if (this.busy || group === this.waistGroup) return;
      const pair = chooseWaistGroup(this.state, group);
      if (!pair) return;
      this.waistGroup = group;
      if (pair.value === this.state.selected.value) {
        this.updateWaistGroups();
      } else {
        this.select('waist', pair.waist, { group });
      }
    }

    select(axis, value, focus = { axis, value }) {
      if (this.busy) return;
      const pair = chooseFit(this.state, axis, value);
      if (!pair) return;
      const input = this.inputs.find(node => node.value === pair.value);
      if (!input || input.checked) return;
      this.pendingFocus = focus;
      this.busy = true;
      this.root.setAttribute('aria-busy', 'true');
      this.root.querySelectorAll('button[data-axis]').forEach(button => { button.disabled = true; });
      this.root.querySelectorAll('[data-waist-group]').forEach(button => { button.disabled = true; });
      this.picker.inert = true;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.timeout = setTimeout(() => {
        if (!this.busy || !this.isConnected) return;
        this.picker.inert = false;
        const original = this.inputs.find(node => node.value === this.state.selected.value);
        if (original) original.checked = true;
        this.busy = false;
        this.pendingFocus = null;
        this.waistGroup = selectedWaistGroup(this.state, this.waistGroup);
        this.root.removeAttribute('aria-busy');
        this.root.querySelectorAll('button[data-axis]').forEach(button => { button.disabled = !chooseFit(this.state, button.dataset.axis, button.dataset.value); });
        this.updateWaistGroups();
        this.root.querySelector('[role="status"]').textContent = this.dataset.retryLabel;
      }, 12000);
    }
  }
  customElements.define('loogu-product-options', LooguProductOptions);
}
